import Constants from 'expo-constants'

import i18n from '@/utils/i18n'
import { toCurrency } from '@/utils/numbers'

export type InternalMoneyRole = 'YBB' | 'CNY'
export type SymbolPosition = 'prefix' | 'suffix'

export interface MoneyPresentation {
  code: string
  displayName: string
  displayDecimals: number
  symbol?: string
  symbolPosition?: SymbolPosition
}

export interface MarketPresentation {
  schemaVersion: 1
  fingerprint?: string
  locales: {
    default: string
    supported: string[]
  }
  branding: {
    displayName: string
    logoUrl?: string
    primaryColor: string
    supportUrl?: string
    websiteUrl: string
  }
  platformToken: MoneyPresentation
  localFiat: MoneyPresentation
}

const legacyBaoBiPresentation: MarketPresentation = {
  schemaVersion: 1,
  locales: {
    default: 'zh-Hans',
    supported: ['zh-Hans', 'zh-Hant']
  },
  branding: {
    displayName: '宝币',
    primaryColor: '#0070DB',
    websiteUrl: 'https://marketing.baobiweb.com/'
  },
  platformToken: {
    code: 'YBB',
    displayName: '宝币',
    displayDecimals: 2
  },
  localFiat: {
    code: 'CNY',
    displayName: '人民币',
    displayDecimals: 2,
    symbol: '¥',
    symbolPosition: 'prefix'
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function nonEmptyString(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value : undefined
}

function displayDecimals(value: unknown, fallback: number) {
  return typeof value === 'number' && Number.isInteger(value) && value >= 0 && value <= 8 ? value : fallback
}

function moneyPresentation(value: unknown, fallback: MoneyPresentation): MoneyPresentation {
  if (!isRecord(value)) return fallback

  const symbolPosition = value.symbolPosition === 'prefix' || value.symbolPosition === 'suffix' ? value.symbolPosition : undefined
  return {
    code: nonEmptyString(value.code) ?? fallback.code,
    displayName: nonEmptyString(value.displayName) ?? fallback.displayName,
    displayDecimals: displayDecimals(value.displayDecimals, fallback.displayDecimals),
    symbol: nonEmptyString(value.symbol) ?? fallback.symbol,
    symbolPosition: symbolPosition ?? fallback.symbolPosition
  }
}

function parseMarketPresentation(value: unknown): MarketPresentation {
  if (!isRecord(value) || value.schemaVersion !== 1) return legacyBaoBiPresentation

  const branding = isRecord(value.branding) ? value.branding : {}
  const locales = isRecord(value.locales) ? value.locales : {}
  const supported = Array.isArray(locales.supported)
    ? locales.supported.filter((locale): locale is string => typeof locale === 'string' && locale.trim().length > 0)
    : legacyBaoBiPresentation.locales.supported

  return {
    schemaVersion: 1,
    fingerprint: nonEmptyString(value.fingerprint),
    locales: {
      default: nonEmptyString(locales.default) ?? legacyBaoBiPresentation.locales.default,
      supported: supported.length > 0 ? supported : legacyBaoBiPresentation.locales.supported
    },
    branding: {
      displayName: nonEmptyString(branding.displayName) ?? legacyBaoBiPresentation.branding.displayName,
      logoUrl: nonEmptyString(branding.logoUrl),
      primaryColor: nonEmptyString(branding.primaryColor) ?? legacyBaoBiPresentation.branding.primaryColor,
      supportUrl: nonEmptyString(branding.supportUrl),
      websiteUrl: nonEmptyString(branding.websiteUrl) ?? legacyBaoBiPresentation.branding.websiteUrl
    },
    platformToken: moneyPresentation(value.platformToken, legacyBaoBiPresentation.platformToken),
    localFiat: moneyPresentation(value.localFiat, legacyBaoBiPresentation.localFiat)
  }
}

export const marketPresentation = parseMarketPresentation(Constants.expoConfig?.extra?.marketPresentation)

export function presentationForRole(role: InternalMoneyRole) {
  return role === 'YBB' ? marketPresentation.platformToken : marketPresentation.localFiat
}

function numericSeparators(locale: string) {
  try {
    const parts = new Intl.NumberFormat(locale).formatToParts(1234567.8)
    return {
      decimal: parts.find((part) => part.type === 'decimal')?.value ?? '.',
      group: parts.find((part) => part.type === 'group')?.value ?? ','
    }
  } catch {
    return { decimal: '.', group: ',' }
  }
}

export function formatMarketNumber(
  amount: string | number | null | undefined,
  role: InternalMoneyRole,
  locale = i18n.resolvedLanguage ?? i18n.language ?? marketPresentation.locales.default
) {
  if (amount === null || amount === undefined) return '-'

  const { displayDecimals } = presentationForRole(role)
  // Backend amounts are decimal strings.  Some legacy callers already apply
  // a comma grouping helper before reaching this boundary, so normalize that
  // presentation-only grouping before formatting it for the active locale.
  const fixed = toCurrency(String(amount).replaceAll(',', ''), displayDecimals)
  if (fixed === '-') return fixed

  const { decimal, group } = numericSeparators(locale)
  const [integer, fraction] = fixed.split('.')
  const localizedInteger = integer.replaceAll(',', group)
  return displayDecimals === 0
    ? localizedInteger
    : `${localizedInteger}${decimal}${fraction ?? '0'.repeat(displayDecimals)}`
}

export function formatTokenAmount(amount: string | number | null | undefined, locale?: string) {
  const presentation = marketPresentation.platformToken
  return `${formatMarketNumber(amount, 'YBB', locale)} ${presentation.displayName}`
}

export function formatTokenAmountRatio(
  completedAmount: string | number | null | undefined,
  listedAmount: string | number | null | undefined,
  locale?: string
) {
  return `${formatMarketNumber(completedAmount, 'YBB', locale)}/${formatTokenAmount(listedAmount, locale)}`
}

export function formatLocalFiatAmount(amount: string | number | null | undefined, locale?: string) {
  const presentation = marketPresentation.localFiat
  const number = amount === '−.−−' ? amount : formatMarketNumber(amount, 'CNY', locale)
  const label = presentation.symbol || presentation.code
  return presentation.symbolPosition === 'suffix' ? `${number} ${label}` : `${label} ${number}`
}

export function formatMarketPrice(amount: string | number | null | undefined, locale?: string) {
  return `${formatLocalFiatAmount(amount, locale)}/${marketPresentation.platformToken.displayName}`
}
